import type { ReportColumn } from '../models/column.types'
import type { ReportRow } from '../models/report.types'

import { DATA_COLLATION_LOCALE } from '@/shared/lib/string'

const DATE_DIMENSIONS = [
  'date',
  'day_of_week',
  'week',
  'day_of_month',
  'day_of_year',
  'month_of_year',
] as const

const DAY_ORDER: Record<string, number> = {
  monday: 1,
  mon: 1,
  pazartesi: 1,
  tuesday: 2,
  tue: 2,
  sali: 2,
  wednesday: 3,
  wed: 3,
  carsamba: 3,
  thursday: 4,
  thu: 4,
  persembe: 4,
  friday: 5,
  fri: 5,
  cuma: 5,
  saturday: 6,
  sat: 6,
  cumartesi: 6,
  sunday: 7,
  sun: 7,
  pazar: 7,
}

const MONTH_ORDER: Record<string, number> = {
  january: 1,
  jan: 1,
  ocak: 1,
  february: 2,
  feb: 2,
  subat: 2,
  march: 3,
  mar: 3,
  mart: 3,
  april: 4,
  apr: 4,
  nisan: 4,
  may: 5,
  mayis: 5,
  june: 6,
  jun: 6,
  haziran: 6,
  july: 7,
  jul: 7,
  temmuz: 7,
  august: 8,
  aug: 8,
  agustos: 8,
  september: 9,
  sep: 9,
  eylul: 9,
  october: 10,
  oct: 10,
  ekim: 10,
  november: 11,
  nov: 11,
  kasim: 11,
  december: 12,
  dec: 12,
  aralik: 12,
}

export interface SortRowsOptions {
  locale?: string
  getColumn?: (field: string) => ReportColumn | null | undefined
}

/** Fold a value to a `DAY_ORDER`/`MONTH_ORDER` key. The `'tr'` here is NOT a collation choice and
 *  is out of ORP-19870's scope — those tables are keyed by the Turkish day/month names the backend
 *  returns, and `toLocaleLowerCase('tr')` is what maps `İ`→`i` and `I`→`ı` correctly for them.
 *  Sorting locale is `DATA_COLLATION_LOCALE`, threaded through `SortRowsOptions.locale`. */
function normalize(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLocaleLowerCase('tr')
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .replaceAll('ı', 'i')
}

function dateDimensionValue(field: string, value: unknown, row: ReportRow): number {
  const year = Number(row.year) || 0
  if (field === 'day_of_week') return DAY_ORDER[normalize(value)] ?? Number.MAX_SAFE_INTEGER
  if (field === 'month_of_year') {
    const month = MONTH_ORDER[normalize(value)] ?? Number(value)
    return Number.isFinite(month) && month > 0 ? year * 100 + month : Number.MAX_SAFE_INTEGER
  }
  if (field === 'day_of_month') return year * 10_000 + Number(value)
  if (field === 'day_of_year') return year * 1_000 + Number(value)
  const timestamp = new Date(String(value)).getTime()
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp
}

function defaultSortField(rows: readonly ReportRow[]): string | null {
  const first = rows[0]
  if (!first) return null
  if (first.date !== undefined) return 'date'
  return DATE_DIMENSIONS.find((field) => first[field] !== undefined) ?? null
}

function compareValues(
  left: unknown,
  right: unknown,
  column: ReportColumn | null | undefined,
  locale: string,
): number {
  if (column?.sub_type === 'array') {
    return (Array.isArray(left) ? left.length : 0) - (Array.isArray(right) ? right.length : 0)
  }
  if (column?.sub_type === 'date' || column?.sub_type === 'datetime') {
    return new Date(String(left)).getTime() - new Date(String(right)).getTime()
  }
  if (typeof left === 'string' && typeof right === 'string') {
    return left.localeCompare(right, locale, { sensitivity: 'base' })
  }
  const numeric = Number(left) - Number(right)
  return Number.isNaN(numeric)
    ? String(left).localeCompare(String(right), locale, { sensitivity: 'base' })
    : numeric
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}

/** Date-aware, nullish-last report sorting. The input array is never mutated. */
export function sortRows(
  rows: readonly ReportRow[],
  sortField?: string | ReportColumn | null,
  sortOrder: 1 | -1 = 1,
  options: SortRowsOptions = {},
): ReportRow[] {
  const field = typeof sortField === 'string' ? sortField : sortField?.field
  const activeField = field ?? defaultSortField(rows)
  if (!activeField) return [...rows]
  const column =
    typeof sortField === 'object' && sortField ? sortField : options.getColumn?.(activeField)
  const isDateDimension = DATE_DIMENSIONS.includes(activeField as (typeof DATE_DIMENSIONS)[number])
  const locale = options.locale ?? DATA_COLLATION_LOCALE

  return [...rows].sort((left, right) => {
    const leftValue = left[activeField]
    const rightValue = right[activeField]
    if (isEmpty(leftValue) && isEmpty(rightValue)) return 0
    if (isEmpty(leftValue)) return 1
    if (isEmpty(rightValue)) return -1
    const comparison = isDateDimension
      ? dateDimensionValue(activeField, leftValue, left) -
        dateDimensionValue(activeField, rightValue, right)
      : compareValues(leftValue, rightValue, column, locale)
    return comparison * sortOrder
  })
}
